<script module lang="ts">
	import type { ISbRichtext, ISbStoryData, SbBlokData } from '@storyblok/js';

	export interface QuoteCarouselItem extends SbBlokData {
		bg_color: string;
		quote: ISbRichtext;
		image: SbImage;
		author: string;
		accolades: string;
	}
</script>

<script lang="ts">
	import SbRichtext from '$lib/storyblok/components/sbRichtext.svelte';
	import type { SbImage } from '$lib/storyblok/types';
	import Image from '$lib/storyblok/components/image.svelte';
	import { fitText } from '$lib/actions/fitText';
	import { editable } from '$lib/storyblok/editable';
	import { addQuotes } from '$lib/utils/quotes';

	interface Props {
		content: ISbStoryData<QuoteCarouselItem>['content'];
		alignment?: 'left' | 'center';
	}

	let { content, alignment = 'left' }: Props = $props();

	let quote = $derived(addQuotes(content.quote, alignment === 'center' ? 'both' : 'append'));

	let imgSize = $derived(alignment === 'center' ? 240 : 491);
</script>

<article
	class={[
		'flex-none basis-full px-5 text-brown-1',
		{
			'flex flex-col items-center py-8': alignment === 'center',
			'pb-16 pt-5': alignment !== 'center'
		}
	]}
	use:editable={content}
	style="background-color: {content.bg_color || '#FFFFFF'};"
>
	<Image
		src={content.image.filename}
		alt={content.image.alt}
		title={content.image.title}
		class={[
			'select-none object-cover',
			{
				'mb-5 aspect-square h-[120px] w-[120px] rounded-xl': alignment === 'center',
				'mb-6 aspect-4/3 rounded-2xl': alignment !== 'center'
			}
		]}
		height={imgSize}
		width={imgSize}
	/>
	<div
		class={[
			'flex flex-col',
			{ 'gap-y-6': alignment !== 'center', 'gap-y-5 text-center': alignment === 'center' }
		]}
	>
		<div
			class={[
				'select-none text-balance',
				{
					'h-[314px]': alignment !== 'center',
					'h-18': alignment === 'center'
				}
			]}
		>
			<div class="relative leading-[140%]" use:fitText={{ minFontSize: 18 }}>
				{#if alignment !== 'center'}
					<span class="absolute -left-[0.67ch] top-0">“</span>
				{/if}
				<SbRichtext text={quote} />
			</div>
		</div>
		<div class="flex select-none flex-col gap-y-1">
			<div
				class={[
					'font-serif text-brown-1',
					{
						'text-[20px]/[124%] tracking-[-.4px] lg:text-[24px]/[120%] lg:tracking-[-.48px]':
							alignment !== 'center',
						'text-[14px]/[160%] lg:text-[20px]/[140%] lg:tracking-[-.2px]': alignment === 'center'
					}
				]}
			>
				{content.author}
			</div>
			<div
				class={[
					'text-brown-1/40',
					{
						'text-[14px]/[112%] max-lg:tracking-[-.14px] lg:text-[16px]/[150%]':
							alignment !== 'center',
						'text-[12px]/[160%]': alignment === 'center'
					}
				]}
			>
				{content.accolades}
			</div>
		</div>
	</div>
</article>
